import type {
  ReportGridFilterModel,
  ResolvedReportPlan,
  ResolvedReportPlanInput,
  ToolApprovalResponse,
} from '@aline/common';

import { getIncludedColumns } from './question-plan/question-plan-helpers';

/** The card's edit state over the proposed plan. */
export interface ReportPlanEdits {
  /** Extraction columns the user took off the plan, by position. */
  excludedIndices: Set<number>;
  filterModel: ReportGridFilterModel;
  plan: ResolvedReportPlan;
}

export type ReportPlanAction = 'createAndRun' | 'createOnly' | 'skip';

/**
 * The response the card sends for the user's decision, which is only ever
 * this plan or no plan. A skip carries nothing. "Create only" and "Create and
 * run" carry the plan with the card's edits applied, which is what the tool
 * creates; "Create and run" also asks for the new report's questions to run.
 * Asking for a different plan is a chat message, not a card action: sending
 * one supersedes the pending approval and starts a new turn.
 */
export function buildReportPlanApprovalResponse({
  action,
  approvalId,
  edits,
  largeRunConfirmed = false,
  messageId,
}: {
  action: ReportPlanAction;
  approvalId: string;
  edits: ReportPlanEdits;
  messageId: string;
  largeRunConfirmed?: boolean;
}): ToolApprovalResponse {
  if (action === 'skip') return { approved: false, id: approvalId, messageId };
  const plan = buildApprovedReportPlan(edits);
  const input: ResolvedReportPlanInput =
    action === 'createAndRun' ? { ...plan, largeRunConfirmed, runAfterCreate: true } : plan;
  return { approved: true, id: approvalId, input, messageId };
}

/**
 * The plan the tool executes once the user approves: the proposal with the
 * card's edits applied. Excluded extraction columns are omitted. Built-in
 * columns remain unchanged because the report contract always includes its
 * default set.
 */
export function buildApprovedReportPlan({
  excludedIndices,
  filterModel,
  plan,
}: ReportPlanEdits): ResolvedReportPlanInput {
  const {
    aiUsageBlockedReason: _aiUsageBlockedReason,
    largeRunConfirmed: _largeRunConfirmed,
    runAfterCreate: _runAfterCreate,
    ...proposed
  } = plan;
  return {
    ...proposed,
    columns: getIncludedColumns(plan.columns, excludedIndices),
    filterModel,
  };
}
